/**
 * `appearance` namespace — the super-admin Appearance panel (ADR 0170/0171).
 */
export const messages = {
  brandLoadFailedTitle: 'Could not load your brand settings',
  brandLoadFailedBody: 'This is a failed read, not a blank brand — the editor is withheld so saving cannot replace your name, logo and colours with placeholders.',
  brandRetry: 'Try again',
  eyebrow: 'Brand',
  title: 'Appearance',
  ledeShort: "Set the app's white-label identity.",
  ledeFull: "Set this installation's colors, type, logo, and name. The accent generates a full, accessible light + dark theme — changes apply live, no rebuild.",
  deniedNotice: 'Appearance is a <1>super-admin</1> surface. Add your tenant id to <3>OPENWOP_SUPERADMIN_TENANTS</3> to edit the app brand.',
  resetToDefault: 'Reset to default',
  resetTitle: 'Reset appearance to default?',
  resetBody: 'This clears the logo, colors, fonts, and name — for everyone using this install.',
  resetLabel: 'Reset',
  resetSuccess: 'Reset to the default identity.',
  save: 'Save',
  saving: 'Saving…',
  saveSuccess: 'Appearance saved.',
  actionFailed: 'Something went wrong. Please try again.',
  quickStart: 'Quick start',
  quickStartHint: 'A starting point, not a fixed theme — change anything after.',
  contrastLabel: 'Contrast:',
  contrastAdvice: 'Lower the contrast target or pick a different accent.',
  contrastCheckLabel: 'Contrast check:',
  contrastBelowAA: 'a token pair falls below WCAG AA — see the Contrast panel. Generated colors are auto-adjusted; advanced overrides are not.',
  generatorOwnedDropped: 'Ignored generator-owned tokens: {{tokens}}. Contrast-bearing colors are solved by the theme generator and cannot be overridden (they would not survive a save).',
  themeHeading: 'Theme',
  brandColorLabel: 'Brand color',
  brandColorHelp: 'Used for buttons, links, and highlights — kept exact, with a readable text shade derived for you.',
  bgTintLabel: 'Background tint',
  bgTintHelp: 'Optional — gives page surfaces a subtle hue. Leave empty for the default warm grey.',
  swatchLabel: '{{label}} swatch',
  seedPlaceholder: 'any CSS color — hex, rgb or oklch',
  contrastFieldLabel: 'Contrast',
  contrastStandard: 'Standard (AA)',
  contrastMedium: 'Medium',
  contrastHigh: 'High',
  cornersLabel: 'Corners',
  cornersDefault: 'Default',
  cornersSharp: 'Sharp',
  cornersMedium: 'Medium',
  cornersRound: 'Round',
  defaultThemeLabel: 'Default theme',
  themeSystem: 'System',
  themeLight: 'Light',
  themeDark: 'Dark',
  fontPairingLabel: 'Font pairing',
  fontCustom: 'Custom / unchanged',
  identityHeading: 'Identity',
  productNameLabel: 'Product name',
  wordmarkPreLabel: 'Wordmark — pre',
  wordmarkEmphasisLabel: 'emphasis',
  wordmarkSubLabel: 'sub',
  documentTitleLabel: 'Document title',
  logoHeading: 'Logo',
  logoUrlLabel: 'Logo / mark URL',
  logoUrlHelp: 'An https URL, a /relative path, or a data:image URI. Upload arrives with Media.',
  faviconLabel: 'Favicon URL',
  advancedSummary: 'Advanced — token override (JSON)',
  advancedHint: 'Export the current theme inputs, or paste a theme JSON to apply. Per-token overrides go under <1>override.light</1> / <3>override.dark</3> (allowlisted tokens only — others are dropped on save).',
  exportCurrent: 'Export current',
  applyJsonBtn: 'Apply JSON',
  themeJsonLabel: 'Theme JSON',
  jsonApplied: 'Theme JSON applied — review the preview, then Save.',
  jsonInvalid: 'Invalid JSON',
  jsonExpectedObject: 'Expected a theme object',
  livePreviewHeading: 'Live preview',
  lightLabel: 'Light',
  darkLabel: 'Dark',
  contrastHeading: 'Contrast',
  contrastHeadingSub: '· WCAG AA · APCA advisory',
  stockThemeNote: 'Stock theme — surfaces meet AA (axe-verified).',
  meetsAA: 'meets AA',
  belowAA: 'below AA',
  previewPrimary: 'Primary action',
  previewSecondary: 'Secondary',
  previewStatus: 'Status',
  previewSurfaceEyebrow: 'Secondary surface',
  previewCardLine: 'A card on <1>--paper-2</1> with a <3>--rule</3> border.',
  previewBody: 'Body text in the brand sans, with an <1>accent link</1>.',
  logoDarkUrlLabel: 'Logo URL (dark mode)',
  logoDarkUrlHelp: 'Optional dark-mode variant — shown when the app renders dark. Leave empty to reuse the main logo.',
  assetUpload: 'Upload image…',
  assetUploading: 'Uploading…',
  assetUploaded: 'Image published — remember to Save.',
} as const;
